/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792454400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Oct 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "nextdoor",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Nextdoor",
          "depth": 0,
          "id": "connect-to-nextdoor"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Conversion events",
          "depth": 1,
          "id": "conversion-events"
        }, {
          "value": "Custom audience",
          "depth": 1,
          "id": "custom-audience"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      ul: "ul",
      li: "li",
      h3: "h3",
      code: "code",
      h4: "h4",
      ol: "ol"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Nextdoor is a neighborhood network where businesses advertise to people in the communities they serve. Hightouch sends conversions from your warehouse to the Nextdoor Conversions API, and uploads your models as Nextdoor custom audiences that your ad groups target or exclude."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Sync Type"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            }), _jsx(_components.th, {
              align: "left",
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              align: "left",
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#conversion-events",
                children: _jsx(_components.strong, {
                  children: "Conversion events"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Send conversions to Nextdoor to measure your ad campaigns"
            }), _jsx(_components.td, {
              align: "left",
              children: "Insert"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.nextdoor.com/reference/conversions-track",
                children: "Track conversions →"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#custom-audience",
                children: _jsx(_components.strong, {
                  children: "Custom audience"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Upload a model as a custom audience that your ad groups target or exclude"
            }), _jsx(_components.td, {
              align: "left",
              children: "All"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.nextdoor.com/reference/post_api-v3-advertisers-advertiserid-audiences",
                children: "Create a custom audience →"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-nextdoor",
        children: "Connect to Nextdoor"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch connects to Nextdoor with an API token. Nextdoor reviews every request for API access: apply with the form on Nextdoor's ", _jsx(_components.a, {
          href: "https://developer.nextdoor.com/reference/applying-for-access",
          children: "Applying for access"
        }), " page, or ask your Nextdoor account manager. After Nextdoor approves the request, generate a token on the ", _jsx(_components.a, {
          href: "https://ads.nextdoor.com/v2/manage/api",
          children: "API page"
        }), " in Nextdoor Ads Manager and enter it as the ", _jsx(_components.strong, {
          children: "API token"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "The two sync types need different access:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Conversion events"
          }), " syncs need a token that can send events to the Conversions API."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom audience"
          }), " syncs need a token that Nextdoor has approved for the Ads API. The token's Nextdoor user also needs a role on each advertiser you sync to that can manage audiences and ad groups, such as an admin or ad manager role."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Testing the connection checks each API separately, as ", _jsx(_components.strong, {
          children: "Conversion events"
        }), " and ", _jsx(_components.strong, {
          children: "Custom audiences"
        }), ". A token that Nextdoor approved only for conversions shows an error on the ", _jsx(_components.strong, {
          children: "Custom audiences"
        }), " check. If you only sync conversion events, you can ignore it. Custom audience syncs fail with that token until Nextdoor approves it for the Ads API."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["When you create a sync, choose what Hightouch sends to Nextdoor: ", _jsx(_components.strong, {
          children: "Conversion events"
        }), " or ", _jsx(_components.strong, {
          children: "Custom audience"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "conversion-events",
        children: "Conversion events"
      }), "\n", _jsxs(_components.p, {
        children: ["Send conversions, such as purchases, leads, and sign-ups, to the Nextdoor ", _jsx(_components.a, {
          href: "https://developer.nextdoor.com/reference/conversion-api",
          children: "Conversions API"
        }), " so Nextdoor can attribute them to your campaigns. Each event needs a client ID, an event name, an event time, an action source, and at least one ", _jsx(_components.code, {
          children: "customer"
        }), " field."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "client-id",
        children: "Client ID"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter your Nextdoor client ID, also known as the NAM advertiser ID. Hightouch sends it as the ", _jsx(_components.code, {
          children: "client_id"
        }), " parameter. To get your client ID, reach out to your Nextdoor account manager. Custom audience syncs don't use the client ID."]
      }), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Choose a static event name, or ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch sends the value as the ", _jsx(_components.code, {
          children: "event_name"
        }), " parameter. The static values are ", _jsx(_components.code, {
          children: "conversion"
        }), ", ", _jsx(_components.code, {
          children: "lead"
        }), ", ", _jsx(_components.code, {
          children: "purchase"
        }), ", ", _jsx(_components.code, {
          children: "sign_up"
        }), ", and ", _jsx(_components.code, {
          children: "custom_conversion_1"
        }), " through ", _jsx(_components.code, {
          children: "custom_conversion_10"
        }), ". If you use a column, its values must be event names Nextdoor supports. See Nextdoor's ", _jsx(_components.a, {
          href: "https://developer.nextdoor.com/reference/supported-conversion-events",
          children: "supported conversion event names"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally, select a column that holds the time each event happened, in ", _jsx(_components.a, {
          href: "https://www.iso.org/iso-8601-date-and-time-format.html",
          children: "ISO 8601"
        }), " format. Hightouch converts it to the format Nextdoor expects and sends it as the ", _jsx(_components.code, {
          children: "event_time"
        }), " parameter. If you leave it empty, Hightouch uses the time the event arrives at the server."]
      }), "\n", _jsx(_components.h4, {
        id: "action-source",
        children: "Action source"
      }), "\n", _jsxs(_components.p, {
        children: ["Choose the channel where the conversion happened, such as ", _jsx(_components.code, {
          children: "website"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), ", or ", _jsx(_components.code, {
          children: "physical_store"
        }), ", or use a column from your model. Hightouch sends it as the ", _jsx(_components.code, {
          children: "action_source"
        }), " parameter. Knowing where your events took place helps Nextdoor show your ads to the right people."]
      }), "\n", _jsx(_components.h4, {
        id: "test-event-codes",
        children: "Test event codes"
      }), "\n", _jsx(_components.p, {
        children: "Test event codes let you check your setup without recording real conversions in Nextdoor. Enter a test event code to send test conversions, and remove it before you run the sync in production."
      }), "\n", _jsx(_components.h4, {
        id: "hashing",
        children: "Hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch hashes the ", _jsx(_components.code, {
          children: "email"
        }), ", ", _jsx(_components.code, {
          children: "phone_number"
        }), ", ", _jsx(_components.code, {
          children: "first_name"
        }), ", ", _jsx(_components.code, {
          children: "last_name"
        }), ", ", _jsx(_components.code, {
          children: "date_of_birth"
        }), ", ", _jsx(_components.code, {
          children: "street_address"
        }), ", ", _jsx(_components.code, {
          children: "city"
        }), ", ", _jsx(_components.code, {
          children: "state"
        }), ", ", _jsx(_components.code, {
          children: "zip_code"
        }), ", and ", _jsx(_components.code, {
          children: "country"
        }), " customer fields with SHA-256 before sending them."]
      }), "\n", _jsxs(_components.p, {
        children: ["If your model already hashes these values, select ", _jsx(_components.strong, {
          children: "No"
        }), " under ", _jsx(_components.strong, {
          children: "Would you like Hightouch to automatically detect if your PII data requires hashing?"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "custom-audience",
        children: "Custom audience"
      }), "\n", _jsxs(_components.p, {
        children: ["Upload a model to Nextdoor as a custom audience that your ad groups target or exclude. Nextdoor's Ads API can't add or remove people from an existing custom audience, and Nextdoor can take ", _jsx(_components.a, {
          href: "https://help.nextdoor.com/s/article/About-Audience-Targeting",
          children: "up to about two days"
        }), " to process each upload. So the sync uploads your whole model as a new audience, limits how many uploads are processing at once, and keeps your ad groups on the newest audience Nextdoor has finished processing."]
      }), "\n", _jsx(_components.h4, {
        id: "how-sync-runs-replace-the-audience",
        children: "How sync runs replace the audience"
      }), "\n", _jsx(_components.p, {
        children: "Each run does the following:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Uploads every row in your model that has a mapped identifier to a new custom audience, unless two of the sync's uploads are still processing, or one that's still processing started less than 20 hours ago."
        }), "\n", _jsx(_components.li, {
          children: "Moves every ad group that uses one of the sync's older audiences to the newest audience Nextdoor has finished processing. An ad group that targets an older audience now targets the newest one, and an ad group that excludes it now excludes the newest one."
        }), "\n", _jsx(_components.li, {
          children: "Archives the sync's older audiences once no ad group uses them. An audience your ad groups move off is archived on the next run."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Nextdoor won't target an audience until it finishes processing it. So the first upload reaches your ad groups on the first run after Nextdoor finishes it, which can take up to about two days. After that, your ad groups stay on the current audience while the next upload processes."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch recognizes the sync's audiences by their description: ", _jsx(_components.code, {
          children: "Managed by Hightouch (workspace <workspace ID>, sync <sync ID>). Each sync run replaces this audience."
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Don't edit the description of a sync's audience in Nextdoor Ads Manager. Once\nthe description changes, Hightouch no longer recognizes the audience: it\ndoesn't move ad groups off it or archive it."
        })
      }), "\n", _jsx(_components.h4, {
        id: "schedule-the-sync",
        children: "Schedule the sync"
      }), "\n", _jsx(_components.p, {
        children: "A schedule more frequent than once a day only uploads more often when Nextdoor processes uploads quickly. Nextdoor can take up to about two days, and runs skip some uploads while earlier ones are processing, as step 1 above describes."
      }), "\n", _jsx(_components.p, {
        children: "A run that doesn't upload still reads your model and moves your ad groups, and the sync run log says why it didn't upload. The run still shows your model's rows as added, even though it sent none of them to Nextdoor."
      }), "\n", _jsx(_components.h4, {
        id: "choose-the-advertiser-and-audience-name",
        children: "Choose the advertiser and audience name"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the advertiser that owns the audience. The list shows the advertisers your API token's user can access in Nextdoor Ads Manager, by name and Ads API advertiser ID. That ID isn't the ", _jsx(_components.strong, {
          children: "Client ID"
        }), " that conversion events syncs use, so pick the advertiser by name."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then enter an audience name, or leave it blank to use the model name. Each upload adds the time its run started, in UTC, to the name, such as ", _jsx(_components.code, {
          children: "Active subscribers (2026-10-19 14:05 UTC)"
        }), ", so you can tell uploads apart in Ads Manager."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Map an email, a postal code, or a street name, or any combination of them. You can map these identifiers:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Street number"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Street name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Unit number"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Postal code"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A street address needs at least the street name. City, state, street number, or unit number alone isn't enough."
      }), "\n", _jsxs(_components.p, {
        children: ["Then select the country your customers live in: ", _jsx(_components.strong, {
          children: "United States"
        }), ", ", _jsx(_components.strong, {
          children: "Canada"
        }), ", or ", _jsx(_components.strong, {
          children: "United Kingdom"
        }), ". Nextdoor ", _jsx(_components.a, {
          href: "https://developer.nextdoor.com/reference/post_api-v3-advertisers-advertiserid-audiences",
          children: "matches each audience"
        }), " against a single country. To reach customers in more than one country, create one sync per country, each with a model filtered to that country."]
      }), "\n", _jsx(_components.h4, {
        id: "add-the-audience-to-ad-groups",
        children: "Add the audience to ad groups"
      }), "\n", _jsxs(_components.p, {
        children: ["Selecting ad groups is optional. Every run moves ad groups that use one of the sync's audiences to the newest audience Nextdoor has finished processing, whether you added the audience in Hightouch or in Nextdoor Ads Manager. Hightouch changes only an ad group's custom audience lists and leaves the rest of its targeting as it is. To have Hightouch add the audience to more ad groups, select them under ", _jsx(_components.strong, {
          children: "Ad groups"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Then choose how those ad groups use the audience:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Target the audience"
          }), ": the ad group reaches only people in the audience who also match its existing custom audience targeting."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exclude the audience"
          }), ": Hightouch adds the audience to every custom audience exclusion on the ad group, so the ad group stops reaching everyone in the audience, in addition to the people it already excludes."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch adds the newest audience Nextdoor has finished processing to these ad groups. For a new sync, that happens on the first run after Nextdoor finishes processing the first upload."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Targeting"
        }), " only applies to an ad group that doesn't use the audience yet, so changing it later doesn't change ad groups that already do. Removing an ad group from ", _jsx(_components.strong, {
          children: "Ad groups"
        }), " doesn't take the audience off it. To stop an ad group from using the audience, remove it from ", _jsx(_components.strong, {
          children: "Ad groups"
        }), " if it's selected, then remove the audience from the ad group in Ads Manager."]
      }), "\n", _jsx(_components.h4, {
        id: "email-hashing",
        children: "Email hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch trims and lowercases each email, then hashes it with SHA-256 before uploading it. Hightouch doesn't hash addresses or postal codes."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch doesn't hash an email that's already a SHA-256 hash again. Nextdoor expects you to trim and lowercase emails before hashing them, so normalize them the same way in your model. See Nextdoor's ", _jsx(_components.a, {
            href: "https://help.nextdoor.com/s/article/Upload-custom-audiences-on-Nextdoor-Ads-Manager",
            children: "custom audience"
          }), " help article."]
        }), "\n", _jsx(_components.li, {
          children: "An email hashed another way, such as with MD5 or SHA-1, can't match on Nextdoor, so Hightouch leaves it out. If the row has no other identifier, Hightouch leaves the row out of the audience."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To upload emails without hashing them, select ", _jsx(_components.strong, {
          children: "No"
        }), " under ", _jsx(_components.strong, {
          children: "Would you like Hightouch to automatically detect if your PII data requires hashing?"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Nextdoor, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nother destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "A model with no usable rows fails the run."
        }), " If a run is due to upload and no row in your model has a mapped identifier, the run fails without uploading and leaves the current audience and your ad groups as they are. This keeps a broken or empty model from emptying your targeting. When only some rows have none of the mapped identifiers, Hightouch leaves those rows out and uploads the rest."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "An ad group Hightouch can't update keeps its audience."
        }), " If Nextdoor rejects the update to an ad group, that ad group stays on the audience it was using, Hightouch doesn't archive that audience, and the run fails with the ad group's ID. Other ad groups still move to the newest audience. The next run tries the ad group again."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Ad groups wait when Nextdoor isn't ready to target an audience."
        }), " Nextdoor sometimes refuses an audience in ad group targeting after marking it active. When it refuses the newest audience, those ad groups keep their current audience, the sync run log names them, and a later run moves them. Hightouch leaves the sync's older audiences in place until the move succeeds, and the run doesn't fail."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "A failed upload fails the run."
        }), " If Nextdoor can't process an upload, the run that finds the failure fails with that audience's ID. If a newer upload has already finished processing, the run doesn't fail. Either way, Hightouch archives the failed audience, and ad groups stay on the newest audience Nextdoor processed. Check the identifier mappings and the country."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "An upload that never finishes stops holding up new ones."
        }), " If Nextdoor is still processing an upload after 7 days, Hightouch stops waiting for it and archives it, so it no longer counts toward the two uploads that can process at once. The sync run log notes it."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Archived and completed ad groups don't change."
        }), " They keep the audience they ended with, but Hightouch doesn't count them as using it, so it can still archive that audience. If you selected one under ", _jsx(_components.strong, {
          children: "Ad groups"
        }), ", the sync run log names it."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Changing the advertiser leaves the previous advertiser's audiences in place."
        }), " Hightouch only manages audiences under the advertiser the sync uses now, and changing the advertiser resets the ", _jsx(_components.strong, {
          children: "Ad groups"
        }), " and ", _jsx(_components.strong, {
          children: "Targeting"
        }), " settings. Archive the previous advertiser's audiences in Ads Manager, and move its ad groups off them."]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsxs(_components.p, {
        children: ["After a custom audience sync runs, check its audiences in Nextdoor Ads Manager under ", _jsx(_components.strong, {
          children: "Assets"
        }), " > ", _jsx(_components.strong, {
          children: "Audience"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Find the newest upload by the upload time at the end of its name. Nextdoor can take up to about two days to process it, and its status shows when it's done."
        }), "\n", _jsx(_components.li, {
          children: "After the first sync run that follows processing, open an ad group that uses the audience and check that its targeting lists that upload."
        }), "\n", _jsx(_components.li, {
          children: "Archived audiences stay in the list, so check each audience's status rather than counting audiences. Besides the audience your ad groups use, expect up to two uploads still processing, and the audience your ad groups just moved off until the next run archives it."
        }), "\n", _jsx(_components.li, {
          children: "If a run didn't upload, the sync run log says why."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
